import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import { Lock, Add, DeleteOutline } from '@mui/icons-material';
import LoadingButton from './LoadingButton';
import { statementPasswordsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';

/**
 * Settings > Email: passwords for banks' statement PDFs. Banks build them
 * from fixed details (e.g. the first 4 letters of the name and the date of
 * birth), so one saved password opens every statement from that bank.
 */
const StatementPasswords = () => {
  const { showSuccess, showError } = useNotification();
  const [passwords, setPasswords] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ label: '', senderDomain: '', password: '' });
  const [saving, setSaving] = useState(false);

  const load = () => statementPasswordsApi.getAll()
    .then(response => setPasswords(response.data.passwords || []))
    .catch(() => setPasswords([]));

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await statementPasswordsApi.create(form);
      showSuccess('Password saved. Locked statements are being opened with it.');
      setDialogOpen(false);
      setForm({ label: '', senderDomain: '', password: '' });
      load();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to save the password');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Forget the password for ${row.label}?`)) return;
    try {
      await statementPasswordsApi.delete(row.id);
      load();
    } catch (error) {
      showError('Failed to delete the password');
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        avatar={<Lock color="action" />}
        title="Statement passwords"
        subheader="Card and bank statements arrive as locked PDFs. Save the password once and their totals and due dates are read automatically."
        action={
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
            Add
          </Button>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {passwords && passwords.length === 0 && (
          <Alert severity="info">
            No passwords saved. You can also open a statement from the Bills card on the dashboard and save its password there.
          </Alert>
        )}
        {passwords && passwords.length > 0 && (
          <List disablePadding>
            {passwords.map((row, index) => (
              <React.Fragment key={row.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={row.label}
                    secondary={[
                      row.senderDomain ? `Statements from ${row.senderDomain}` : 'Tried on all statements',
                      row.lastUsedAt ? `last used ${new Date(row.lastUsedAt).toLocaleDateString()}` : 'not used yet'
                    ].join(' · ')}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Forget this password">
                      <IconButton edge="end" size="small" onClick={() => handleDelete(row)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add statement password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Bank or card"
              placeholder="e.g. HDFC Infinia"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              fullWidth
            />
            <TextField
              label="Sender (optional)"
              placeholder="e.g. hdfcbank.net"
              helperText="Statements from this sender are tried with this password first"
              value={form.senderDomain}
              onChange={(e) => setForm({ ...form, senderDomain: e.target.value })}
              fullWidth
            />
            <TextField
              label="PDF password"
              type="password"
              autoComplete="off"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              fullWidth
            />
            <Typography variant="body2" color="text.secondary">
              The bank's statement email says how the password is made. It is stored encrypted and never shown again.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <LoadingButton variant="contained" onClick={handleSave} loading={saving} disabled={!form.label.trim() || !form.password}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default StatementPasswords;
